// src/hooks/useBranches.js
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { GetAllBranches, GetBranchById, AddNewBranch, UpdateBranch, DeleteBranch } from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';

// Hook for fetching all branches
export function useGetBranches() {
  return useQuery('branches', GetAllBranches, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => {
      // Transform the data for easier consumption in components
      return data?.data?.branches?.map(branch => ({
        _id: branch._id,
        branchName: branch.name,
        branchPhone: branch.phone,
        email: branch.email,
        buildingCount: branch.buildingCount || "NA",
        address: branch.address,
        city: branch.city,
        state: branch.state,
        postalCode: branch.postalCode,
        country: branch.country,
        lng: branch.location?.lng,
        lat: branch.location?.lat,
        googleMapsLink: branch.location?.googleMapsLink,
        website: branch.website,
      })) || [];
    },
    onError: (error) => {
      NotifyError("Failed to fetch branches", error);
      console.error("Error fetching branches:", error);
    }
  });
}

// Hook for fetching a single branch by ID
export function useGetBranchById(branchId) {
  return useQuery(
    ['branch', branchId],
    () => GetBranchById(branchId),
    {
      enabled: !!branchId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: (error) => {
        NotifyError("Failed to fetch branch details", error);
        console.error("Error fetching branch details:", error);
      }
    }
  );
}

// Hook for adding a new branch
export function useAddBranch() {
  const queryClient = useQueryClient();

  return useMutation(
    (newBranch) => AddNewBranch(newBranch),
    {
      onSuccess: () => {
        Notify("Branch added successfully");
        queryClient.invalidateQueries('branches');
      },
      onError: (error) => {
        NotifyError("Failed to add branch", error);
        console.error("Error adding branch:", error);
      }
    }
  );
}

// Hook for updating a branch
export function useUpdateBranch() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }) => UpdateBranch(id, data),
    {
      onSuccess: (data, variables) => {
        Notify("Branch updated successfully");
        queryClient.invalidateQueries('branches');
        queryClient.invalidateQueries(['branch', variables.id]);
      },
      onError: (error) => {
        NotifyError("Failed to update branch", error);
        console.error("Error updating branch:", error);
      }
    }
  );
}

// Hook for deleting a branch
export function useDeleteBranch() {
  const queryClient = useQueryClient();

  return useMutation(
    (branchId) => DeleteBranch(branchId),
    {
      onSuccess: () => {
        Notify("Branch deleted successfully");
        queryClient.invalidateQueries('branches');
      },
      onError: (error) => {
        NotifyError("Failed to delete branch", error);
        console.error("Error deleting branch:", error);
      }
    }
  );
}